export function isNumeric(str: string): boolean {
  return !isNaN(Number(str)) && !isNaN(parseFloat(str));
}

export function ellipsizeText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }

  const ellipsis = "...";
  const halfMaxLength = Math.floor((maxLength - ellipsis.length) / 2);
  const start = text.slice(0, halfMaxLength);
  const end = text.slice(text.length - halfMaxLength);

  return `${start}${ellipsis}${end}`;
}

export function getFileName(fileName: string): string {
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex === -1) {
    return fileName;
  } else {
    return fileName.substring(0, dotIndex);
  }
}

export function getFileExt(fileName: string): string {
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex === -1) {
    return "";
  } else {
    return fileName.substring(dotIndex + 1);
  }
}

export function isInvalidDiscountAndCredit(
  discount: number,
  creditNotePercentage: number
): boolean {
  if (
    typeof discount === "number" &&
    !isNaN(discount) &&
    discount >= 0 &&
    discount <= 100 &&
    typeof creditNotePercentage === "number" &&
    !isNaN(creditNotePercentage) &&
    creditNotePercentage >= 0
  ) {
    return false;
  }
  return true;
}

export function buildAddress({
  firstLine,
  secondLine,
  city,
  state,
  pincode,
}: {
  firstLine: string;
  secondLine?: string;
  city: string;
  state: string;
  pincode: string;
}) {
  let addressLine = "";
  if (
    firstLine &&
    firstLine.trim() !== "" &&
    secondLine &&
    secondLine.trim() !== ""
  ) {
    addressLine = `${firstLine} ${secondLine}`;
  } else if (firstLine) {
    addressLine = firstLine;
  } else if (secondLine) {
    addressLine = secondLine;
  }
  const parts = [addressLine, city, state, pincode];
  const nonEmptyParts = parts.filter((part) => part && part.trim() !== "");
  return nonEmptyParts.join(", ");
}

export const computeDelayFromNow = (value?: string | null) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  const diffMs = Date.now() - parsed.getTime();
  if (diffMs < 0) return "Scheduled";

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  if (diffMinutes < 1) return "Just now";

  const days = Math.floor(diffMinutes / (60 * 24));
  const hours = Math.floor((diffMinutes % (60 * 24)) / 60);
  const minutes = diffMinutes % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  return parts.join(" ");
};

export const sanitizePhoneNumber = (input?: string | null) => {
  if (!input) return "";
  return input.replace(/[^0-9+]/g, "");
};

export const formatMobileNumber = (input?: string | null) => {
  const sanitized = sanitizePhoneNumber(input);
  if (!sanitized) return "";

  const digitsOnly = sanitized.replace(/\D/g, "");
  if (digitsOnly.length === 0) return sanitized;

  // 10 digit number
  if (digitsOnly.length === 10) {
    return `+91 ${digitsOnly}`; // ✅ only one space after country code
  }

  // 12 digit with country code
  if (digitsOnly.length === 12 && digitsOnly.startsWith("91")) {
    return `+91 ${digitsOnly.slice(2)}`; // ✅ remove 91 and add proper format
  }

  return sanitized;
};

/** Clipboard: 10-digit national number (drops leading 91 when present). */
export const getNationalMobileForCopy = (input?: string | null): string => {
  if (!input) return "";

  // Remove all non-digit characters
  let digits = String(input).replace(/\D/g, "");

  // Remove country code (India: 91) if present
  if (digits.startsWith("91") && digits.length > 10) {
    digits = digits.slice(2);
  }

  // Always return last 10 digits
  return digits.slice(-10);
};
